import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminAbuseReport } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Filter } from "@/ui/Filter";
import { Chip, type ChipTone } from "@/ui/Chip";
import { Button } from "@/ui/Button";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { ReasonDialog } from "@/features/admin/ReasonDialog";
import { useFilters } from "@/features/admin/useFilters";
import { absoluteTime, cx, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/abuse")({
  component: AbuseScreen,
});

const STATES = ["", "received", "reviewing", "upheld", "rejected"] as const;

type Decision = "reviewing" | "upheld" | "rejected";

const DECISIONS: Record<Decision, { label: string; title: string; description: string; confirm: string }> = {
  reviewing: {
    label: "Take it",
    title: "Start looking at this report",
    description: "Says somebody has it. The note is for whoever picks it up after you.",
    confirm: "Take it",
  },
  upheld: {
    label: "Uphold",
    title: "Uphold this report",
    description:
      "The report was right and something was done. Say what, because this is the answer the reporter gets and the record if anybody asks later.",
    confirm: "Uphold it",
  },
  rejected: {
    label: "Reject",
    title: "Reject this report",
    description:
      "The report was wrong, or not about anything hosted here. Say which, in words a stranger could read.",
    confirm: "Reject it",
  },
};

/**
 * Notices that something hosted here should not be.
 *
 * # WHY OPEN REPORTS SORT FIRST AND OLDEST WITHIN THAT
 *
 * Because the oldest unanswered report is the one that matters, and a list in
 * the usual newest-first order buries exactly the one that has been waiting
 * since Tuesday.
 *
 * Every resolution carries a reason. That is not bureaucracy: it is the answer
 * the reporter is owed, and the record if an authority ever asks what this
 * platform did about a notice it was sent.
 */
function AbuseScreen() {
  const filters = useFilters();
  const state = filters.get("state");
  const [acting, setActing] = useState<{ report: AdminAbuseReport; to: Decision } | null>(null);
  const [subject, setSubject] = useState<AdminAbuseReport | null>(null);

  const reports = useQuery({
    queryKey: ["platform-abuse", state],
    queryFn: ({ signal }) =>
      api.get<{ reports: AdminAbuseReport[] | null }>(
        "/admin/abuse-reports",
        { state: state || undefined, limit: 300 },
        signal,
      ),
    refetchInterval: 60000,
  });

  const list = reports.data?.reports ?? [];
  const open = list.filter((r) => r.state === "received" || r.state === "reviewing");
  const untouched = list.filter((r) => r.state === "received");
  const official = untouched.filter((r) => r.official);

  return (
    <div className="flex flex-col gap-4">
      {untouched.length > 0 && (
        <div
          className={cx(
            "rounded-xl border px-4 py-3",
            official.length > 0
              ? "border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]"
              : "border-[color-mix(in_oklab,_var(--warning)_35%,_transparent)] bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]",
          )}
        >
          <p
            className={cx(
              "text-[14px] font-medium",
              official.length > 0
                ? "text-[var(--danger)]"
                : "text-[var(--warning)]",
            )}
          >
            {untouched.length === 1
              ? "One report nobody has looked at"
              : `${untouched.length} reports nobody has looked at`}
            {official.length > 0 &&
              `, ${official.length} of them from somebody claiming to act officially`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            The oldest has been waiting since {shortTime(open[0]?.created_at)}.
          </p>
        </div>
      )}

      <Panel
        toolbar={
          <>
            {STATES.map((s) => (
              <Filter
                key={s || "all"}
                label={s || "all"}
                active={state === s}
                onClick={() => filters.set("state", s)}
              />
            ))}
          </>
        }
        padded={false}
      >
        {reports.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={5} columns={6} />
          </div>
        ) : reports.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={reports.error} onRetry={() => void reports.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              title={state ? `Nothing is ${state}` : "No reports"}
              description="Nobody has reported anything hosted here."
            />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[24%]">Reported</Th>
                  <Th width="w-[13%]">Category</Th>
                  <Th width="w-[16%]">About</Th>
                  <Th width="w-[14%]">Reporter</Th>
                  <Th width="w-[11%]">State</Th>
                  <Th width="w-[9%]" align="right">
                    Sent
                  </Th>
                  <Th width="w-[13%]" align="right">
                    Actions
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <Tr key={r.id} className={rowTone(r)}>
                    <Td clip title={`${r.url}\n\n${r.detail}`}>
                      <span className="mono text-[13px]">{r.url}</span>
                    </Td>
                    <Td clip title={r.detail}>
                      <Chip tone={r.category === "csam" || r.category === "terrorism" ? "down" : undefined}>
                        {r.category.replace(/_/g, " ")}
                      </Chip>
                    </Td>
                    {/* What it turned out to be about. Empty until somebody
                        works it out, which is a step of its own. */}
                    <Td clip title={r.service_name || undefined}>
                      {r.organisation_id ? (
                        <Link
                          to="/admin/$orgId"
                          params={{ orgId: r.organisation_id }}
                          className="hover:text-[var(--primary)]"
                        >
                          {r.organisation_name}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setSubject(r)}
                          className="text-[13px] text-[var(--primary)] hover:underline"
                        >
                          not yet linked
                        </button>
                      )}
                    </Td>
                    <Td clip title={r.reporter_email || r.source_ip || undefined}>
                      {r.official && (
                        <Chip tone="warn" className="mr-1.5">
                          official
                        </Chip>
                      )}
                      <span className="text-[var(--text-secondary)]">
                        {r.reporter_email || r.reporter_name || "anonymous"}
                      </span>
                    </Td>
                    <Td clip title={r.decision || undefined}>
                      <Chip tone={stateTone(r.state)}>{r.state}</Chip>
                    </Td>
                    <Td align="right" title={absoluteTime(r.created_at)}>
                      {shortTime(r.created_at)}
                    </Td>
                    <Td align="right">
                      <div className="flex items-center justify-end gap-1">
                        {r.state === "received" && (
                          <Button
                            tone="secondary"
                            size="dense"
                            onClick={() => setActing({ report: r, to: "reviewing" })}
                          >
                            {DECISIONS.reviewing.label}
                          </Button>
                        )}
                        {(r.state === "received" || r.state === "reviewing") && (
                          <>
                            <Button
                              tone="secondary"
                              size="dense"
                              onClick={() => setActing({ report: r, to: "upheld" })}
                            >
                              {DECISIONS.upheld.label}
                            </Button>
                            <Button
                              tone="secondary"
                              size="dense"
                              onClick={() => setActing({ report: r, to: "rejected" })}
                            >
                              {DECISIONS.rejected.label}
                            </Button>
                          </>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      {acting && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setActing(null)}
          title={DECISIONS[acting.to].title}
          description={`${DECISIONS[acting.to].description}\n\n${acting.report.url}`}
          prompt="Decision"
          confirmLabel={DECISIONS[acting.to].confirm}
          danger={acting.to === "upheld"}
          action={(decision) =>
            api.post<void>(`/admin/abuse-reports/${acting.report.id}/decide`, {
              state: acting.to,
              decision,
            })
          }
          onDone={async () => {
            setActing(null);
            await reports.refetch();
          }}
        />
      )}

      {subject && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setSubject(null)}
          title="Which account is this about?"
          description={`Paste the organisation identifier. The search in the command palette finds it from the hostname.\n\n${subject.url}`}
          prompt="Organisation identifier"
          hint="Twenty-six characters. Leave it empty if the report is not about anything hosted here."
          confirmLabel="Link it"
          action={(orgId) =>
            api.put<void>(`/admin/abuse-reports/${subject.id}/subject`, {
              organisation_id: orgId.trim(),
            })
          }
          onDone={async () => {
            setSubject(null);
            await reports.refetch();
          }}
        />
      )}
    </div>
  );
}

function stateTone(
  state: AdminAbuseReport["state"],
): ChipTone | undefined {
  switch (state) {
    case "received":
      return "warn";
    case "reviewing":
      return "live";
    // Upheld is the loud one. It means something hosted here was wrong and was
    // acted on, which is the row somebody will come back to.
    case "upheld":
      return "down";
    default:
      return undefined;
  }
}

function rowTone(r: AdminAbuseReport): string | undefined {
  if (r.state !== "received") return undefined;
  if (r.official || r.category === "csam" || r.category === "terrorism") {
    return "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]";
  }
  return "bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]";
}
